// Copyright 2021 The Prometheus Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { CompletionContext, CompletionResult } from '@codemirror/autocomplete';
import { HTTPPrometheusClient, PrometheusClient, PrometheusConfig } from '../client/prometheus';
import { HybridComplete } from './hybrid';

// Complete is the interface that defines the simple method that returns a CompletionResult.
// Every different completion mode must implement this interface.
export interface CompleteStrategy {
  promQL(context: CompletionContext): Promise<CompletionResult | null> | CompletionResult | null;
}

export type VariableCompletionKind = 'label' | 'interval' | 'range' | 'text';

export interface VariableCompletion {
  // The name of the variable.
  name: string;
  // The prometheus label name.
  labelName?: string;
  // The semantic variable type, used to decide which PromQL contexts can use it.
  kind?: VariableCompletionKind;
}

// CompleteConfiguration should be used to customize the autocompletion.
export interface CompleteConfiguration {
  remote?: PrometheusConfig | PrometheusClient;
  // maxMetricsMetadata is the maximum number of metrics in Prometheus for which metadata is fetched.
  // If the number of metrics exceeds this limit, no metric metadata is fetched at all.
  maxMetricsMetadata?: number;
  // When providing this custom CompleteStrategy, the settings above will not be used.
  completeStrategy?: CompleteStrategy;
  // enableBuiltInVariables enables autocompletion of built-in variables like $__rate_interval
  // in duration contexts (e.g., inside square brackets).
  enableBuiltInVariables?: boolean;
  // Variables (e.g. $namespace) can be passed from context-aware consumers.
  // labelName is optional metadata used to rank suggestions in label value contexts.
  variables?: VariableCompletion[];
}

export function isPrometheusClient(
  remoteConfig: PrometheusConfig | PrometheusClient
): remoteConfig is PrometheusClient {
  const client = remoteConfig as PrometheusClient;
  return (
    typeof client.labelNames === 'function' &&
    typeof client.labelValues === 'function' &&
    typeof client.metricMetadata === 'function' &&
    typeof client.series === 'function' &&
    typeof client.metricNames === 'function'
  );
}

export function newCompleteStrategy(conf?: CompleteConfiguration): CompleteStrategy {
  if (conf?.completeStrategy) {
    return conf.completeStrategy;
  }
  if (conf?.remote) {
    if (isPrometheusClient(conf.remote)) {
      return new HybridComplete(conf.remote, conf.maxMetricsMetadata, conf.enableBuiltInVariables, conf.variables);
    }
    return new HybridComplete(
      new HTTPPrometheusClient(conf.remote),
      conf.maxMetricsMetadata,
      conf.enableBuiltInVariables,
      conf.variables
    );
  }
  return new HybridComplete(undefined, undefined, conf?.enableBuiltInVariables, conf?.variables);
}
